import { Button, Icon, Image, InlineStack, Paragraph, Stack, Text } from '@customerio/pluma-components/react';
import { FileInput } from '@/components/file-input';
import type { SignResponse } from '@/services/uploads';
import { isEmptyString } from '@/utils/string';
import type { ImportSource, SheetSource } from './types';
import { GoogleSheetsSourceButton } from './google-sheets-source-button';
import { DEFAULT_ACCEPTED_EXTENSIONS, fileInputLabel, maxFileSizeLabel, type AcceptedExtension } from './file-rules';
import { useSourceUpload } from './use-source-upload';
import googleSheetsLogo from '@/assets/images/integrations/google-sheets.svg';

export type { AcceptedExtension } from './file-rules';

type ImportSourceFieldProps = {
  value: ImportSource | null;
  onChange: (source: ImportSource | null) => void;
  workspaceId: number;
  maxFileSizeBytes: number | undefined;
  /**
   * Notifies the owner while an upload is in flight. During a replace, `value`
   * still points at the previous key, so a form that only checks for a non-null
   * source would submit stale import data.
   */
  onUploadingChange: ((isUploading: boolean) => void) | undefined;
  /**
   * Signs the upload. Each surface has its own sign endpoint answering its own shape, so the
   * caller adapts it to the shared one rather than this field knowing which endpoint it serves.
   * The returned policy decides the bucket, so it also decides which uploader runs.
   */
  signUpload: (file: { size: number; name: string; contentType: string }) => Promise<SignResponse>;
  /**
   * The file types this surface takes. Narrower than the default for CSV imports, whose pipeline
   * reads only CSV — accepting a JSON there would sign and upload a file it cannot process.
   */
  acceptedExtensions?: readonly AcceptedExtension[];
  /**
   * Something the surface has to say about what it does with the file, shown beside the size
   * limit. Only CSV imports has one — the acceptable-use notice — and it is the surface's to
   * word rather than this field's.
   */
  notice?: React.ReactNode;
  /**
   * Told when the reader throws the attached file away, or asks to swap it for another.
   *
   * The field owns those two controls but not what they mean to the surface: CSV imports reports
   * each as its own event, and collections reports neither. `kind` is what the surface calls the
   * source it is losing, which is the only thing the field knows that the surface does not.
   */
  onSourceDiscarded?: (change: { reason: 'deleted' | 'replaced'; kind: ImportSource['kind'] }) => void;
  /**
   * Closes every control the field owns. The surface says why: CSV imports passes the reader's
   * lack of `journeys/imports/create`, because an upload they could never turn into an import
   * still signs a policy and writes an object into the workspace's bucket.
   */
  isDisabled?: boolean;
};

// The collections endpoints only ever return the sheet ids (ui_api/collections/index.go), so the
// display names exist solely on a source picked in this session.
function describeSheet(sheet: SheetSource): string {
  if (isEmptyString(sheet.sheet_name) || isEmptyString(sheet.spreadsheet_name)) {
    return `Sheet ID ${sheet.spreadsheet_id}`;
  }
  return `${sheet.sheet_name} from Google Sheet ${sheet.spreadsheet_name}`;
}

export function ImportSourceField({
  value,
  onChange,
  workspaceId,
  maxFileSizeBytes,
  onUploadingChange,
  signUpload,
  acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
  notice,
  onSourceDiscarded,
  isDisabled = false,
}: ImportSourceFieldProps) {
  const { isUploading, error, selectFile, selectSheet, clearSource, reportReplacing } = useSourceUpload({
    onChange,
    onUploadingChange,
    signUpload,
    acceptedExtensions,
    maxFileSizeBytes,
    onSourceDiscarded,
  });
  // Both reasons a control of this field is closed: the surface closed it, or an upload it
  // started is still running.
  const isControlDisabled = isDisabled || isUploading;

  if (value != null && value.kind === 's3') {
    return (
      <AttachedFile
        filename={value.filename}
        accept={acceptedExtensions.map((ext) => `.${ext}`).join(',')}
        isDisabled={isControlDisabled}
        error={error}
        onFileSelected={(file, viaDragAndDrop) => void selectFile(file, viaDragAndDrop)}
        onDelete={() => clearSource('s3')}
        onReplace={() => reportReplacing('s3')}
      />
    );
  }

  if (value != null && value.kind === 'sheet') {
    return (
      <AttachedSheet
        sheet={value.sheet}
        workspaceId={workspaceId}
        isDisabled={isControlDisabled}
        onSelect={selectSheet}
        onDelete={() => clearSource('sheet')}
        onReplace={() => reportReplacing('sheet')}
      />
    );
  }

  return (
    <Stack gap="100">
      <FileInput
        label={fileInputLabel(acceptedExtensions)}
        browseLabel="Choose file"
        accept={acceptedExtensions.map((ext) => `.${ext}`).join(',')}
        isDisabled={isControlDisabled}
        error={error ?? undefined}
        onFileSelected={(file, { viaDragAndDrop }) => void selectFile(file, viaDragAndDrop)}
        action={
          <GoogleSheetsSourceButton workspaceId={workspaceId} onSelect={selectSheet} isDisabled={isControlDisabled} />
        }
      />
      <InlineStack alignInline="space-between" gap="200">
        <Text color="subtle" size="xs">
          {notice}
        </Text>
        {maxFileSizeBytes != null ? (
          <Text color="subtle" size="xs" flexShrink="0">
            Maximum size: {maxFileSizeLabel(maxFileSizeBytes)}
          </Text>
        ) : null}
      </InlineStack>
    </Stack>
  );
}

type AttachedFileProps = {
  filename: string;
  accept: string;
  isDisabled: boolean;
  error: string | null;
  onFileSelected: (file: File, viaDragAndDrop: boolean) => void;
  onDelete: () => void;
  onReplace: () => void;
};

/** The file that is attached, with the two ways to be rid of it. */
function AttachedFile({ filename, accept, isDisabled, error, onFileSelected, onDelete, onReplace }: AttachedFileProps) {
  return (
    <FileInput
      label={`Replace ${filename}`}
      accept={accept}
      isDisabled={isDisabled}
      error={error ?? undefined}
      onFileSelected={(file, { viaDragAndDrop }) => onFileSelected(file, viaDragAndDrop)}
      variant="plain"
    >
      {({ openFilePicker }) => (
        <Stack gap="200">
          <InlineStack alignBlock="center" gap="150">
            <InlineStack alignBlock="center" gap="100" backgroundColor="subtle" p="200">
              <Icon name="download" />
              <Text>{filename}</Text>
            </InlineStack>
            <Icon name="check" color="success" />
          </InlineStack>
          <InlineStack gap="100">
            <Button variant="secondary" isCritical={true} isDisabled={isDisabled} onClick={onDelete}>
              Delete file
            </Button>
            <Button
              variant="secondary"
              isDisabled={isDisabled}
              onClick={() => {
                onReplace();
                openFilePicker();
              }}
            >
              Replace
            </Button>
          </InlineStack>
        </Stack>
      )}
    </FileInput>
  );
}

type AttachedSheetProps = {
  sheet: SheetSource;
  workspaceId: number;
  isDisabled: boolean;
  onSelect: (sheet: SheetSource) => void;
  onDelete: () => void;
  onReplace: () => void;
};

/** The sheet that is attached. No file input: a sheet is picked, not uploaded. */
function AttachedSheet({ sheet, workspaceId, isDisabled, onSelect, onDelete, onReplace }: AttachedSheetProps) {
  return (
    <Stack gap="100">
      <InlineStack alignBlock="center" gap="050">
        <Image src={googleSheetsLogo} alt="" width={16} height={16} />
        <Paragraph>{describeSheet(sheet)}</Paragraph>
      </InlineStack>
      <InlineStack gap="100">
        <GoogleSheetsSourceButton
          workspaceId={workspaceId}
          onSelect={onSelect}
          onOpen={onReplace}
          isDisabled={isDisabled}
        >
          Replace
        </GoogleSheetsSourceButton>
        <Button variant="secondary" isDisabled={isDisabled} onClick={onDelete}>
          Remove
        </Button>
      </InlineStack>
    </Stack>
  );
}
